import React, { useMemo, useEffect, useRef, useState, useCallback, memo } from 'react';
import {
  IonGrid,
  IonRow,
  IonCol,
  IonItem,
  IonText,
  IonChip,
  IonRippleEffect,
  IonCard,
  IonCardContent,
  useIonRouter
} from '@ionic/react';
import { format, addDays } from 'date-fns';
import { BookingItem, ProcessedBooking } from '@rsmaids/shared/types/bookings';
import styles from './DayView.module.scss';
import { useBusinessHours } from '../../hooks/useBusinessHours';
import { serviceColorMap } from '@rsmaids/shared/types/services';
import { motion, useMotionValue,useTransform } from 'motion/react';
import { formatTo12Hour } from '@rsmaids/shared/utils/timeUtils';
import { DataStorageService } from '@rsmaids/shared/services/DataStorageService';
import { useTranslation } from 'react-i18next';
import { translateDynamic } from '../../i18n/dynamic';
import { dfLocale } from '../../i18n/locale';

interface DayViewProps {
  currentDate: Date;
  bookings: BookingItem[];
  onBookingSelect?: (booking: BookingItem) => void;
  completionStatuses?: Record<string, boolean>;
  onDateChange?: (date: Date) => void;
}


const AnimatedIonRow = motion.create(IonRow);

function toDate(val: string | Date): Date {
  if (val instanceof Date) return val;
  return new Date(val);
}

const DayView: React.FC<DayViewProps> = ({
  currentDate,
  bookings,
  onBookingSelect,
  completionStatuses = {},
  onDateChange,
}) => {
  
  const containerRef = useRef<HTMLIonCardElement>(null);
  const startXRef = useRef(0);
  const isDraggingRef = useRef(false);
  const isSwipingRef = useRef(false);
  const isVerticalScrollingRef = useRef(false);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const x = useMotionValue(0);
  const xTransform = useTransform(x, (value: number) => `translateX(calc(-33.333% + ${value}px))`);
  const animatedStyle = {
    transform: xTransform,
    willChange: 'transform',
    backfaceVisibility: 'hidden' as const,
    perspective: 1000
  };


  const [isAnimating, setIsAnimating] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const timeIndicatorRef = useRef<HTMLIonRowElement>(null);
  const timeGridRef = useRef<HTMLIonGridElement>(null);


  useEffect(() => {
    const updateCurrentTime = () => setCurrentTime(new Date());
    updateCurrentTime();
    const intervalId = setInterval(updateCurrentTime, 60000);
    return () => clearInterval(intervalId);
  }, []);


  const currentTimePosition = useMemo(() => {
    const hours = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    const totalMinutes = (hours * 60 + minutes) + 15;
    return totalMinutes * (24 / 15);
  }, [currentTime]);


  // The grid follows the current time only during business hours (today's real opening
  // hours from the business profile). Outside them it sits with opening time at the top.
  const { openMinutes, closeMinutes } = useBusinessHours(currentTime);
  useEffect(() => {
    const ROW_PX = 24; // one 15-minute row
    const businessStartPosition = (openMinutes / 15) * ROW_PX;
    const nowMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
    const inBusinessHours = nowMinutes >= openMinutes && nowMinutes < closeMinutes;
    const top = inBusinessHours
      ? Math.max(businessStartPosition, currentTimePosition - 200)
      : businessStartPosition;

    // Applied now and again once the view has finished laying out / animating in: right
    // after switching views the grid can still be settling, which made a single early
    // assignment get lost. behavior 'auto' = jump (the grid's CSS default is smooth).
    const apply = () => {
      const timeGrid = timeIndicatorRef.current?.closest(`.${styles.timeGrid}`);
      timeGrid?.scrollTo({ top, behavior: 'auto' });
    };
    apply();
    const frame = requestAnimationFrame(apply);
    const settle = setTimeout(apply, 350);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(settle);
    };
  }, [currentTimePosition, currentTime, openMinutes, closeMinutes]);


  const safeCurrentDate = useMemo(() => {
    const date = currentDate || new Date();
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }, [currentDate]);


  const { prevDay, nextDay } = useMemo(() => ({
    prevDay: addDays(safeCurrentDate, -1),
    nextDay: addDays(safeCurrentDate, 1)
  }), [safeCurrentDate]);


  const timeSlots = useMemo(() => {
    const slots = [];
    const prevDay = addDays(safeCurrentDate, -1);

    slots.push({
      hour: 23,
      minute: 45,
      isHourStart: false,
      isHalfHour: false,
      isQuarterHour: true,
      displayHour: '',
      key: '23-45',
      isMidnight: false,
      isLastHour: false,
      date: prevDay
    });

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        if (hour === 23 && minute === 45) continue;

        slots.push({
          hour,
          minute,
          isHourStart: minute === 0,
          isHalfHour: minute === 30,
          isQuarterHour: minute === 15 || minute === 45,
          displayHour: minute === 0 ?
            (hour === 0 ? '12AM' :
              hour < 12 ? `${hour}AM` :
                hour === 12 ? '12PM' :
                  `${hour - 12}PM`) : '',
          key: `${hour}-${minute}`,
          isMidnight: hour === 0 && minute === 0,
          isLastHour: hour === 23,
          date: safeCurrentDate
        });
      }
    }

    return slots;
  }, [safeCurrentDate]);


  const handleTouchStart = (e: React.TouchEvent) => {
    if (isAnimating) {
      return;
    }
    startXRef.current = e.touches[0].clientX;
    const isTimeGrid = e.currentTarget === timeGridRef.current;
    if (!isTimeGrid) {
      isDraggingRef.current = true;
      isSwipingRef.current = false;
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isAnimating) {
      return;
    }
    const currentX = e.touches[0].clientX;
    const diffX = currentX - startXRef.current;
    if (Math.abs(diffX) > 15 && !isVerticalScrollingRef.current) {
      isDraggingRef.current = true;
      isSwipingRef.current = true;
      x.set(diffX);
    } else if (isDraggingRef.current && isSwipingRef.current) {
      x.set(diffX);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (isAnimating) {
      isDraggingRef.current = false;
      isSwipingRef.current = false;
      return;
    }
    if (isDraggingRef.current && isSwipingRef.current && onDateChange) {
      const currentX = e.changedTouches[0].clientX;
      const diffX = currentX - startXRef.current;
      const threshold = 50;
      if (Math.abs(diffX) > threshold) {
        // Just change the date without animation
        const newDate = addDays(safeCurrentDate, diffX > 0 ? -1 : 1);
        setIsAnimating(true);
        // Immediately reset position and change date
        x.set(0);
        setIsAnimating(false);
        onDateChange(newDate);
      } else {
        // Simple reset without animation
        x.set(0);
      }
    } else {
      // Reset position
      x.set(0);
    }
    isDraggingRef.current = false;
    isSwipingRef.current = false;
  };

  const handleScroll = useCallback(() => {
    if (!isSwipingRef.current) {
      isVerticalScrollingRef.current = true;
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      scrollTimeout.current = setTimeout(() => {
        isVerticalScrollingRef.current = false;
      }, 200);
    }
  }, []);


  const bookingCache = useRef<Record<string, ProcessedBooking[]>>({});

  useEffect(() => {
    bookingCache.current = {};
  }, [bookings]);


  const visibleBookings = useMemo(
    () => bookings.filter(b => {
      const status = String(b.status).toLowerCase();
      return status !== 'canceled' && status !== 'cancelled';
    }),
    [bookings]
  );


  const getBookingsForSlot = useCallback((
    date: Date,
    slot: { hour: number, minute: number, key: string }
  ): (ProcessedBooking & { columnIndex: number; totalColumns: number })[] => {
    const slotStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), slot.hour, slot.minute, 0, 0);
    const slotEnd = new Date(slotStart.getTime() + 15 * 60 * 1000);

    return visibleBookings
      .filter(booking => {
        const bookingStart = toDate(booking.startTime);
        return bookingStart >= slotStart && bookingStart < slotEnd;
      })
      .sort((a, b) => toDate(a.startTime).getTime() - toDate(b.startTime).getTime())
      .map((booking, index, arr) => {
        return {
          ...booking,
          columnIndex: index,
          totalColumns: arr.length
        };
      });
  }, [visibleBookings]);


  const BookingItem = memo(({
    booking,
    onSelect
  }: {
    booking: ProcessedBooking & { bookingOffsetMinutes?: number },
    onSelect?: (booking: BookingItem) => void
  }) => {
  const { t } = useTranslation();
    const ionRouter = useIonRouter();
    const isCompleted = completionStatuses[booking.id] || booking.status === 'completed';

    const handleCardClick = useCallback((event: React.MouseEvent) => {
      event.stopPropagation();
      if (onSelect) {
        onSelect(booking);
        return;
      }

      DataStorageService.storeData('selectedBooking', booking, false);
      ionRouter.push(`/bookingSystem/booking/${booking.id}`, 'forward', 'push');
    }, [booking, onSelect, ionRouter]);

    const [isPressed, setIsPressed] = useState(false);
    const rippleRef = useRef<HTMLIonRippleEffectElement>(null);
    const serviceColor = serviceColorMap[booking.serviceType as keyof typeof serviceColorMap] || 'var(--ion-color-primary)';
    const height = calculateBookingHeight(booking.startTime, booking.endTime);
    const isShortSlot = height === '24px' || height === '16px' || parseInt(height) <= 24;
    const cssVars = {
      '--booking-height': height,
      '--booking-bg-color': serviceColor,
      '--booking-left': `${(booking.columnIndex * 40) / booking.totalColumns}%`,
      '--booking-width': `${80 / booking.totalColumns}%`,
      transform: isPressed ? 'translateZ(0) scale(0.97)' : 'translateZ(0) scale(1)',
      transition: 'transform 150ms'
    } as React.CSSProperties;



    const handleTouchStart = (event: React.TouchEvent) => {
      setIsPressed(true);
      if (rippleRef.current) {
        const x = event.touches[0].clientX;
        const y = event.touches[0].clientY;
        rippleRef.current.addRipple(x, y);
      }
    };

    const handleTouchEnd = () => setIsPressed(false);


    const isBlocked = booking.type === 'blocked';
    const isOpaque = isBlocked && booking.transparency === 'OPAQUE';


    if (isBlocked) {
      const blockedLabel = translateDynamic(booking.agendaItem?.title || 'Blocked Time');
      return (
        <IonCard
          className={`${styles.bookingCard} ${styles.blockedSlot} ${isOpaque ? styles.opaque : ''}`}
          style={cssVars}
          onClick={handleCardClick}
          button
        >
          <IonCardContent className={styles.bookingContent}>
            <IonText className={styles.clientName}>{blockedLabel}</IonText>
          </IonCardContent>
        </IonCard>
      );
    }

    return (
      <IonCard
        key={booking.id}
        className={`${styles.bookingCard} ${isShortSlot ? styles.shortSlot : ''} ${isCompleted ? styles.completed : ''}`}
        onClick={handleCardClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        button={false}
        style={cssVars}
        data-booking-id={booking.id}
      >
        <IonRippleEffect ref={rippleRef} />
        <IonCardContent className={styles.bookingContent}>
          <IonText className={styles.bookingName}>
            {booking.contact?.name || booking.title || t('pages.bookingCalendar.DayView.unnamed')}
          </IonText>
          <IonText className={styles.bookingService}>
            {translateDynamic(booking.serviceType || '')}
          </IonText>
          <IonText className={styles.bookingTime}>
            {formatTo12Hour(formatTimeToString(booking.startTime))} - {formatTo12Hour(formatTimeToString(booking.endTime))}
          </IonText>
          {isCompleted && (
            <IonText className={styles.completedIndicator}>✓</IonText>
          )}
        </IonCardContent>
      </IonCard>
    );
  });


  const calculateBookingHeight = (startTime: string | Date, endTime: string | Date): string => {
    let startHour: number, startMinute: number, endHour: number, endMinute: number;

    if (typeof startTime === 'string') {
      [startHour, startMinute] = startTime.split(':').map(Number);
    } else {
      startHour = startTime.getHours();
      startMinute = startTime.getMinutes();
    }

    if (typeof endTime === 'string') {
      [endHour, endMinute] = endTime.split(':').map(Number);
    } else {
      endHour = endTime.getHours();
      endMinute = endTime.getMinutes();
    }

    const totalStartMinutes = startHour * 60 + startMinute;
    const totalEndMinutes = endHour * 60 + endMinute;
    const durationInMinutes = totalEndMinutes - totalStartMinutes;

    return `${(durationInMinutes * (24 / 15))}px`;
  };

  const formatTimeToString = (time: string | Date): string => {
    if (typeof time === 'string') {
      return time;
    } else {
      return time.toTimeString().substring(0, 5);
    }
  };


  return (
    <IonCard
      ref={containerRef}
      className={styles.dayView}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        '--touch-action': isAnimating ? 'none' : 'pan-y',
      } as React.CSSProperties}
    >
      {/* Time grid */}
      <IonGrid className={styles.timeGrid} ref={timeGridRef} onScroll={handleScroll}>
        {/* Background grid layer */}
        <IonRow className={styles.backgroundGridLayer}>
          <IonCol size="2" className={styles.timeColSpacer}></IonCol>
          <IonCol className={styles.gridLinesWrapper}>
            <AnimatedIonRow
              className={styles.animatedGridLines}
              style={animatedStyle}
            >
              {/* Previous day column */}
              <IonCol className={styles.gridCol}>
                {timeSlots.map(slot => (
                  <IonRow
                    key={`grid-prev-${slot.key}`}
                    className={`${styles.gridRow} 
                      ${slot.isHourStart ? styles.hourStart : ''}
                      ${slot.isHalfHour ? styles.halfHour : ''}
                      ${slot.isQuarterHour ? styles.quarterHour : ''}
                      ${((slot.hour === 23 && slot.minute === 45) || (slot.hour >= 0 && slot.hour < 6) || (slot.hour >= 18 && slot.hour <= 23)) ? styles.offHours : ''}`}
                  >
                  </IonRow>
                ))}
              </IonCol>

              {/* Current day column */}
              <IonCol className={styles.gridCol}>
                {timeSlots.map(slot => (
                  <IonRow
                    key={`grid-current-${slot.key}`}
                    className={`${styles.gridRow} 
                      ${slot.isHourStart ? styles.hourStart : ''}
                      ${slot.isHalfHour ? styles.halfHour : ''}
                      ${slot.isQuarterHour ? styles.quarterHour : ''}
                      ${((slot.hour === 23 && slot.minute === 45) || (slot.hour >= 0 && slot.hour < 6) || (slot.hour >= 18 && slot.hour <= 23)) ? styles.offHours : ''}`}
                  >
                  </IonRow>
                ))}
              </IonCol>

              {/* Next day column */}
              <IonCol className={styles.gridCol}>
                {timeSlots.map(slot => (
                  <IonRow
                    key={`grid-next-${slot.key}`}
                    className={`${styles.gridRow} 
                      ${slot.isHourStart ? styles.hourStart : ''}
                      ${slot.isHalfHour ? styles.halfHour : ''}
                      ${slot.isQuarterHour ? styles.quarterHour : ''}
                      ${((slot.hour === 23 && slot.minute === 45) || (slot.hour >= 0 && slot.hour < 6) || (slot.hour >= 18 && slot.hour <= 23)) ? styles.offHours : ''}`}
                  >
                  </IonRow>
                ))}
              </IonCol>
            </AnimatedIonRow>
          </IonCol>
        </IonRow>

        {/* Current time indicator */}
        <IonRow
          className={styles.currentTimeOverlay}
          style={{
            top: `${currentTimePosition}px`,
            transform: 'translateY(-50%)'
          }}
          ref={timeIndicatorRef}
        >
          <IonCol size="2" className={styles.timeTextCol}>
            <IonText color="primary" className={styles.currentTimeText}>
              {format(currentTime, 'h:mm a', { locale: dfLocale() })}
            </IonText>
          </IonCol>
          <IonCol className={styles.lineContainer}>
            <IonChip color="primary" className={styles.timeIndicatorDot}></IonChip>
            <IonItem lines="none" className={styles.timeIndicatorLine} />
          </IonCol>
        </IonRow>

        {/* Time grid content */}
        <IonRow className={styles.timeGridContent}>
          {/* Time column */}
          <IonCol size="2" className={styles.timeColumnContainer}>
            <IonGrid className={styles.timeLabelsGrid}>
              {timeSlots.map(slot => {
                const now = currentTime;
                const currentHour = now.getHours();
                const currentMinute = now.getMinutes();
                const isCloseToCurrentTime =
                  slot.isHourStart && (
                    (slot.hour === currentHour + 1 && currentMinute >= 52) ||
                    (slot.hour === currentHour && currentMinute <= 8)
                  );

                return (
                  <IonRow key={`time-${slot.key}`} className={styles.timeLabelRow}>
                    <IonCol className={styles.timeLabelCol}>
                      {slot.isHourStart && !isCloseToCurrentTime && (
                        <IonText className={styles.timeLabel}>
                          {slot.displayHour}
                        </IonText>
                      )}
                    </IonCol>
                  </IonRow>
                );
              })}
            </IonGrid>
          </IonCol>

          {/* Day columns with bookings */}
          <IonCol className={styles.dayGridContainer}>
            <IonGrid className={styles.dayGrid}>
              {timeSlots.map((slot) => (
                <IonRow key={slot.key} className={styles.timeRow}>
                  <IonCol className={styles.dayColumnsWrapper}>
                    <AnimatedIonRow
                      className={styles.allDayColumns}
                      style={animatedStyle}
                    >
                      {/* Previous day */}
                      <IonCol className={styles.dayColumn}>
                        {getBookingsForSlot(prevDay, slot).map((booking: ProcessedBooking) => (
                          <BookingItem
                            key={booking.id}
                            booking={booking}
                            onSelect={onBookingSelect}
                          />
                        ))}
                      </IonCol>

                      {/* Current day */}
                      <IonCol className={styles.dayColumn}>
                        {getBookingsForSlot(safeCurrentDate, slot).map((booking: ProcessedBooking) => {
                          return (
                            <BookingItem
                              key={booking.id}
                              booking={booking}
                              onSelect={onBookingSelect}
                            />
                          );
                        })}
                      </IonCol>

                      {/* Next day */}
                      <IonCol className={styles.dayColumn}>
                        {getBookingsForSlot(nextDay, slot).map((booking: ProcessedBooking) => (
                          <BookingItem
                            key={booking.id}
                            booking={booking}
                            onSelect={onBookingSelect}
                          />
                        ))}
                      </IonCol>
                    </AnimatedIonRow>
                  </IonCol>
                </IonRow>
              ))}
            </IonGrid>
          </IonCol>
        </IonRow>
      </IonGrid>
    </IonCard>
  );
};

export default React.memo(DayView, (prevProps, nextProps) => {
  return prevProps.currentDate.getTime() === nextProps.currentDate.getTime() &&
    prevProps.bookings === nextProps.bookings &&
    prevProps.onBookingSelect === nextProps.onBookingSelect &&
    prevProps.completionStatuses === nextProps.completionStatuses &&
    prevProps.onDateChange === nextProps.onDateChange;
});